import { create } from 'zustand';
import type {
  User,
  Organization,
  QRTokenContext,
  Case,
  Team,
  QRPoint,
  Toast
} from '../types';

interface AppState {
  // Current User
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;

  // QR Context (for worker flow)
  qrContext: QRTokenContext | null;

  // Cases
  cases: Case[];

  // Teams
  teams: Team[];

  // QR Points
  qrPoints: QRPoint[];

  // UI State
  isLoading: boolean;
  toasts: Toast[];

  // Actions
  setUser: (user: User | null) => void;
  /**
   * 認証セッション確定時に、ユーザーと所属組織を1回の更新でまとめて反映する。
   *
   * setUser と setOrganization を2回に分けて呼ぶと isAuthenticated と organization が
   * 別レンダーで立ち上がり、両方に依存する読み込み処理（App の loadDashboardData）が
   * 二重に走る。認証復元経路では必ずこちらを使う。
   */
  setAuthenticatedUser: (user: User | null, org: Organization | null) => void;
  setOrganization: (org: Organization | null) => void;
  setQRContext: (ctx: QRTokenContext | null) => void;
  setCases: (cases: Case[]) => void;
  addCase: (caseData: Case) => void;
  updateCase: (id: string, updates: Partial<Case>) => void;
  deleteCase: (id: string) => void;
  setTeams: (teams: Team[]) => void;
  setQRPoints: (qrPoints: QRPoint[]) => void;
  addQRPoint: (qrPoint: QRPoint) => void;
  updateQRPoint: (id: string, updates: Partial<QRPoint>) => void;
  deleteQRPoint: (id: string) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  // Initial State
  user: null,
  organization: null,
  isAuthenticated: false,
  qrContext: null,
  cases: [],
  teams: [],
  qrPoints: [],
  isLoading: false,
  toasts: [],

  // User Actions
  setUser: (user) => set({
    user,
    isAuthenticated: !!user
  }),

  setAuthenticatedUser: (user, organization) => set({
    user,
    organization,
    isAuthenticated: !!user
  }),

  setOrganization: (organization) => set({ organization }),

  setQRContext: (qrContext) => set({ qrContext }),

  // Cases Actions
  setCases: (cases) => set({ cases }),

  addCase: (caseData) => set((state) => ({
    cases: [caseData, ...state.cases]
  })),

  updateCase: (id, updates) => set((state) => ({
    cases: state.cases.map((c) =>
      c.id === id ? { ...c, ...updates } : c
    )
  })),

  deleteCase: (id) => set((state) => ({
    cases: state.cases.filter((c) => c.id !== id)
  })),

  // Teams Actions
  setTeams: (teams) => set({ teams }),

  // QR Points Actions
  setQRPoints: (qrPoints) => set({ qrPoints }),

  addQRPoint: (qrPoint) => set((state) => ({
    qrPoints: [...state.qrPoints, qrPoint]
  })),

  updateQRPoint: (id, updates) => set((state) => ({
    qrPoints: state.qrPoints.map((qr) =>
      qr.id === id ? { ...qr, ...updates } : qr
    )
  })),

  deleteQRPoint: (id) => set((state) => ({
    qrPoints: state.qrPoints.filter((qr) => qr.id !== id)
  })),

  // Toast Actions
  addToast: (toast) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }]
    }));

    // Auto-remove after duration
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
  },

  removeToast: (id) => set((state) => ({
    toasts: state.toasts.filter((t) => t.id !== id)
  })),

  // UI Actions
  setLoading: (isLoading) => set({ isLoading }),

  // Logout
  logout: () => set({
    user: null,
    organization: null,
    isAuthenticated: false,
    cases: [],
    teams: [],
    qrPoints: []
  })
}));

// Selector hooks for common patterns
export const useUser = () => useAppStore((state) => state.user);
export const useOrganization = () => useAppStore((state) => state.organization);
export const useIsAuthenticated = () => useAppStore((state) => state.isAuthenticated);
export const useCases = () => useAppStore((state) => state.cases);
export const useToasts = () => useAppStore((state) => state.toasts);
export const useIsLoading = () => useAppStore((state) => state.isLoading);
